import { erroresPorCampo, numero, traducirError, useTraduccion, type ProblemaApi } from '@tallerya/i18n';
import { Ayuda, AvisoError, Boton, IconoMas, useAvisos } from '@tallerya/ui';
import { useId, useRef, useState, type ChangeEvent } from 'react';
import {
  esImagen,
  TIPOS_ACEPTADOS,
  useAdjuntos,
  useCambiosAdjuntos,
  useUrlAdjunto,
  type Adjunto,
  type Origen,
} from './datos';

// Las fotos y archivos de un activo (B-26), del comprobante de un gasto (#143) o
// de una orden y sus líneas (#140): la galería, subir y eliminar.
//
// Recibe de quién son y nada más; lo que se muestre alrededor es de su pantalla:
// la ficha del activo, la ventana del gasto o la ficha de la orden.
//
// Compacto a propósito: miniaturas chicas en una grilla que llena el ancho que le
// den. La ficha del activo tiene más cosas que mostrar que sus fotos.

interface Props {
  /** De quién cuelgan. */
  origen: Origen;
  /** El título de la galería. Sin él, «Fotos y archivos». */
  titulo?: string;
  /** La explicación tras el ⓘ del título, como las demás secciones de una ficha (#120). */
  info?: string;
  /**
   * Lo que dice cada foto debajo, en lugar de su nombre de archivo: en la orden, de
   * qué línea es (#140). `undefined` deja el nombre.
   */
  etiquetaDe?: (adjunto: Adjunto) => string | undefined;
  /** «4 fotos» bajo el título, como la sección «Fotos» de la orden en el Figma. */
  conCuenta?: boolean;
  /** Sin «Agregar» ni «×»: quien solo puede ver lo que acompaña (#140). */
  soloLectura?: boolean;
}

export function Adjuntos({ origen, titulo, info, etiquetaDe, conCuenta = false, soloLectura = false }: Props) {
  const { t, idioma } = useTraduccion();
  const { avisar } = useAvisos();
  const adjuntos = useAdjuntos(origen);
  const cambios = useCambiosAdjuntos(origen);
  const idTitulo = useId();
  const selector = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(0);
  const [problema, setProblema] = useState<string | null>(null);

  /** El texto de un rechazo: el del campo si la API señaló uno, si no el general. */
  function textoDe(error: ProblemaApi): string {
    const [primero] = Object.values(erroresPorCampo(error, t)).flat();
    if (primero) return primero;
    return error.code ? traducirError(error.code, t) : t('ui.formulario.aviso.sinRespuesta');
  }

  async function subir(evento: ChangeEvent<HTMLInputElement>) {
    const archivos = [...(evento.target.files ?? [])];
    // Se limpia para que volver a elegir el mismo archivo dispare el cambio.
    evento.target.value = '';
    if (archivos.length === 0) return;

    setProblema(null);
    setSubiendo((n) => n + archivos.length);

    // De a uno: varias fotos grandes en paralelo se pelean la misma conexión y
    // terminan todas más tarde que en fila.
    //
    // Una petición que no llega a tener respuesta —la red que se corta, el
    // servidor que cierra a medio cuerpo— rechaza en vez de devolver el error. El
    // contador baja igual en el `finally`: si no, el botón quedaría «subiendo»
    // hasta recargar.
    for (const archivo of archivos) {
      try {
        const error = await cambios.subir(archivo);
        if (error) setProblema(`${archivo.name}: ${textoDe(error)}`);
      } catch {
        setProblema(`${archivo.name}: ${t('ui.formulario.aviso.sinRespuesta')}`);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  }

  async function eliminar(adjunto: Adjunto) {
    setProblema(null);
    let error: ProblemaApi | undefined;
    try {
      error = await cambios.eliminar(String(adjunto.id));
    } catch {
      setProblema(t('ui.formulario.aviso.sinRespuesta'));
      return;
    }
    if (error) {
      setProblema(textoDe(error));
      return;
    }
    avisar({ titulo: t('admin.adjuntos.aviso.eliminado', { nombre: adjunto.fileName }) });
  }

  return (
    <section className="adjuntos" aria-labelledby={idTitulo}>
      <header className="adjuntos__encabezado">
        <div>
          <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
            <h2 id={idTitulo}>{titulo ?? t('admin.adjuntos.titulo')}</h2>
            {info && <Ayuda tema={titulo ?? t('admin.adjuntos.titulo')} texto={info} tono="titulo" />}
          </div>
          {conCuenta && adjuntos.data && adjuntos.data.length > 0 && (
            <p className="tipos__resumen">{t('admin.adjuntos.cuantas', { n: adjuntos.data.length })}</p>
          )}
        </div>
        {!soloLectura && (
          <>
            <Boton
              variante="secundario"
              icono={<IconoMas />}
              disabled={subiendo > 0}
              aria-busy={subiendo > 0}
              onClick={() => selector.current?.click()}
            >
              {subiendo > 0 ? t('admin.adjuntos.subiendo', { n: subiendo }) : t('admin.adjuntos.agregar')}
            </Boton>
            <input
              ref={selector}
              type="file"
              accept={TIPOS_ACEPTADOS}
              multiple
              hidden
              onChange={(e) => void subir(e)}
            />
          </>
        )}
      </header>

      {problema && <AvisoError aviso={{ titulo: t('admin.adjuntos.error.titulo'), texto: problema }} />}

      {adjuntos.isPending && <p className="adjuntos__nota">{t('admin.adjuntos.cargando')}</p>}
      {adjuntos.isError && <p className="adjuntos__nota">{t('admin.adjuntos.errorLista')}</p>}
      {adjuntos.data?.length === 0 && <p className="adjuntos__nota">{t('admin.adjuntos.vacio')}</p>}

      {adjuntos.data && adjuntos.data.length > 0 && (
        <ul className="adjuntos__grilla">
          {adjuntos.data.map((a) => (
            <Miniatura
              key={String(a.id)}
              origen={origen}
              adjunto={a}
              etiqueta={etiquetaDe?.(a)}
              tamano={numero(Math.ceil(Number(a.byteSize) / 1024), idioma)}
              {...(!soloLectura && { onEliminar: () => void eliminar(a) })}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface PropsMiniatura {
  origen: Origen;
  adjunto: Adjunto;
  /** Lo que se lee debajo; sin ella, el nombre del archivo. */
  etiqueta: string | undefined;
  /** En KB, ya formateado. */
  tamano: string;
  /** Sin ella no hay «×». */
  onEliminar?: () => void;
}

/**
 * Una foto o un archivo. Pide su propia URL firmada: la lista no las trae, y así
 * solo se firma lo que de verdad se dibuja.
 */
function Miniatura({ origen, adjunto, etiqueta, tamano, onEliminar }: PropsMiniatura) {
  const { t } = useTraduccion();
  const url = useUrlAdjunto(origen, String(adjunto.id));
  const direccion = url.data?.url;

  return (
    <li className="adjuntos__item">
      <a
        className="adjuntos__vista"
        href={direccion}
        target="_blank"
        rel="noreferrer"
        aria-disabled={!direccion}
        title={adjunto.fileName}
      >
        {esImagen(adjunto) && direccion ? (
          <img src={direccion} alt={adjunto.fileName} loading="lazy" />
        ) : (
          <span className="adjuntos__tipo">{extension(adjunto.fileName)}</span>
        )}
      </a>
      <div className="adjuntos__pie">
        <span className="adjuntos__nombre">{etiqueta ?? adjunto.fileName}</span>
        <small>{t('admin.adjuntos.tamano', { kb: tamano })}</small>
      </div>
      {onEliminar && (
        <Boton
          variante="texto"
          className="adjuntos__eliminar"
          aria-label={t('admin.adjuntos.eliminar', { nombre: adjunto.fileName })}
          onClick={onEliminar}
        >
          ×
        </Boton>
      )}
    </li>
  );
}

/** «PDF», «HEIC»: lo que se ve en lugar de la miniatura cuando no hay imagen. */
function extension(nombre: string): string {
  const punto = nombre.lastIndexOf('.');
  return punto >= 0 ? nombre.slice(punto + 1).toUpperCase() : '—';
}
